import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

// ─── Types ───────────────────────────────────────────

export interface Payment {
  id: string;
  periodStart: string;
  periodEnd: string;
  amount: string;
  type: string;
  description: string | null;
  paidAt: string | null;
  createdByName: string;
  createdAt: string;
}

export interface CommissionSummary {
  totalOs: number;
  totalRevenue: number;
  commissionRate: number;
  commissionAmount: number;
  alreadyPaid: number;
  balance: number;
}

export interface ScheduleEntry {
  id: string;
  dayOfWeek: number;
  startTime: string;
  endTime: string;
  active: boolean;
}

export interface ScheduleTodayMechanic {
  id: string;
  name: string;
  startTime: string;
  endTime: string;
  absent: boolean;
  absenceType: string | null;
}

export interface ScheduleTodayData {
  scheduledToday: number;
  activeToday: number;
  absentToday: number;
  mechanics: ScheduleTodayMechanic[];
}

// ─── Hooks ───────────────────────────────────────────

export function useMechanicPayments(mechanicId: string, year?: string) {
  return useQuery<{ payments: Payment[] }>({
    queryKey: ['mechanic-payments', mechanicId, year],
    queryFn: async () => {
      const params = year ? `?year=${year}` : '';
      const res = await api.get(`/workshops/my/mechanics/${mechanicId}/payments${params}`);
      return res.data;
    },
  });
}

export function useCommissionSummary(mechanicId: string, month: string) {
  return useQuery<CommissionSummary>({
    queryKey: ['commission-summary', mechanicId, month],
    queryFn: async () => {
      const res = await api.get(
        `/workshops/my/mechanics/${mechanicId}/commission-summary?month=${month}`,
      );
      return res.data;
    },
  });
}

export function useCreatePayment(mechanicId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      periodStart: string;
      periodEnd: string;
      amount: number;
      type: 'fixed' | 'commission' | 'advance';
      description?: string;
    }) => {
      const res = await api.post(`/workshops/my/mechanics/${mechanicId}/payments`, data);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mechanic-payments', mechanicId] });
      queryClient.invalidateQueries({ queryKey: ['commission-summary', mechanicId] });
    },
  });
}

export function useMarkPaid(mechanicId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (paymentId: string) => {
      const res = await api.patch(
        `/workshops/my/mechanics/${mechanicId}/payments/${paymentId}/mark-paid`,
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mechanic-payments', mechanicId] });
      queryClient.invalidateQueries({ queryKey: ['commission-summary', mechanicId] });
    },
  });
}

export function useMechanicSchedule(mechanicId: string) {
  return useQuery<{ entries: ScheduleEntry[] }>({
    queryKey: ['mechanic-schedule', mechanicId],
    queryFn: async () => {
      const res = await api.get(`/workshops/my/mechanics/${mechanicId}/schedule`);
      return res.data;
    },
  });
}

export function useReplaceSchedule(mechanicId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (entries: Array<{
      dayOfWeek: number;
      startTime: string;
      endTime: string;
      active: boolean;
    }>) => {
      const res = await api.put(`/workshops/my/mechanics/${mechanicId}/schedule`, { entries });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mechanic-schedule', mechanicId] });
      queryClient.invalidateQueries({ queryKey: ['schedule-today'] });
    },
  });
}

export function useCreateAbsence(mechanicId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      date: string;
      type: 'vacation' | 'sick' | 'personal';
      notes?: string;
    }) => {
      const res = await api.post(`/workshops/my/mechanics/${mechanicId}/absences`, data);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schedule-today'] });
    },
  });
}

export function useScheduleToday() {
  return useQuery<ScheduleTodayData>({
    queryKey: ['schedule-today'],
    queryFn: async () => {
      const res = await api.get('/workshops/my/schedule/today');
      return res.data;
    },
  });
}
